import { useMutation } from '@apollo/client';
import React from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';

import { type TemplateBundleOption } from './BundleDefaultRowForm';
import { BundleDefaultsPanelHeader } from './BundleDefaultsPanelHeader';
import {
  getTenantUsersForPersonalSettingsQuery,
  updateUserPersonalDefaultBundleMutation,
} from './bundle-defaults.operations';
import { getTemplateBundleDisplayName } from './template-bundle.operations';

const NONE_VALUE = '__none__';

interface PersonalSettingsPaneProps {
  tenantId: string;
  userId: string;
  userName: string;
  currentBundleId: string | null;
  bundles: TemplateBundleOption[];
}

export const PersonalSettingsPane: React.FC<PersonalSettingsPaneProps> = ({
  tenantId,
  userId,
  userName,
  currentBundleId,
  bundles,
}) => {
  const toast = useToast();

  const [updateDefaultBundle, { loading }] = useMutation(
    updateUserPersonalDefaultBundleMutation,
    {
      context: eluveAdminHasuraContext,
      // Refetch the user list so the pane (and any UI keyed on the cached
      // settings row) reflects the new value without the admin having to
      // navigate away and back.
      refetchQueries: [
        {
          query: getTenantUsersForPersonalSettingsQuery,
          variables: { tenantId },
          context: eluveAdminHasuraContext,
        },
      ],
      onError: (error) => {
        toast.error(`Failed to update default bundle: ${error.message}`);
      },
    },
  );

  const handleChange = (value: string) => {
    const nextId = value === NONE_VALUE ? null : value;
    if (nextId === currentBundleId) return;
    void updateDefaultBundle({
      variables: { tenantId, userId, defaultTemplateBundleId: nextId },
      optimisticResponse: {
        updateTenantUserSettingsByPk: {
          __typename: 'TenantUserSettings' as const,
          tenantId,
          userId,
          defaultTemplateBundleId: nextId,
        },
      },
    });
  };

  const handleClear = () => handleChange(NONE_VALUE);

  // Surface a bundle the user is pointing at that is no longer enabled for the
  // tenant so the admin sees the issue rather than the dropdown silently
  // rendering "No default set". The orphan is shown as a disabled option that
  // the admin must replace.
  const currentBundleIsMissing =
    currentBundleId !== null && !bundles.some((b) => b.id === currentBundleId);

  return (
    <Stack align="stretch">
      <BundleDefaultsPanelHeader
        sticky
        cornerRadius="tr"
        title="Personal Settings"
        action={
          currentBundleId !== null ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </Button>
          ) : undefined
        }
      />

      <Stack gap={3} align="stretch" className="px-3 py-3">
        <Text size="xs" color="tertiary">
          {userName}&apos;s personal template preferences
        </Text>

        <Stack gap={1} align="stretch">
          <Text size="xs">Default Template Bundle</Text>
          <Select
            value={currentBundleId ?? NONE_VALUE}
            onValueChange={handleChange}
            disabled={loading}
          >
            <SelectTrigger
              className="text-text-primary"
              aria-label="Default Template Bundle"
            >
              <SelectValue placeholder="No default set" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>
                <Text size="xs" color="tertiary">
                  No default set
                </Text>
              </SelectItem>
              {currentBundleIsMissing && currentBundleId && (
                <SelectItem value={currentBundleId} disabled>
                  <Text size="xs" color="tertiary">
                    Current (no longer available)
                  </Text>
                </SelectItem>
              )}
              {bundles.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {getTemplateBundleDisplayName(b)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentBundleIsMissing && (
            <Text size="xs" color="tertiary">
              This user&apos;s saved default points to a bundle that is no
              longer enabled for the tenant. Pick a different bundle or clear.
            </Text>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
